/**
 * React Query hooks for all server-state management.
 *
 * These replace Zustand stores (useLibraryStore, useStatsStore) for server data.
 * Client-only state (board, game, analysis) stays in Zustand.
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listUserGames,
  getShowcasePlayers,
  getShowcasePlayer,
  getUserProfile,
  updateUserProfile,
  saveGame as apiSaveGame,
  listAnalyzedIds,
  getGame,
} from '../../utils/apiConfig';
import type { GamesView } from '../../utils/apiConfig';
// Not the components/AuthWrapper barrel: it re-exports AuthButtons, whose account menu reads these hooks, so going
// through it is an import cycle (in dev, React Refresh touched AuthButtons before it existed and the app went blank).
import { useAuthToken } from '../../features/auth/useAuthToken';
import { staleTimes } from '../../theme';
import { queryKeys } from '../../utils/queryKeys';
import { firstPage, needsOlderGames, olderPage, type SinceRead } from './gamesSince';
import { mergePage, PAGE_RETRY_MS, retried, seedFromPrevious } from './gamesPaging';
import type { UserProfile, UpdateUserRequest } from '../../types/user';
import type { GameRecord, ShowcasePlayer, ShowcasePlayerDetail } from '../../types/game';

// Re-exported for existing importers; canonical definition is utils/queryKeys.ts.
export { queryKeys };

// ============================================================================
// GAMES
// ============================================================================

// Two page sizes, on purpose:
// FIRST_PAGE_SIZE (small) is the ONLY thing first paint waits on. The full list is ~5.7 KB/game, so a
// 500-game first page is ~3 MB before anything renders (measured) — that's the "takes forever" blank.
// A 25-game first page is ~140 KB → cards appear in ~1s; the newest games sort first anyway, which is
// what this page is for. GAMES_PAGE_SIZE (large) then back-fills the REST in the background at the
// backend's MAX_PAGE_LIMIT, so the full library still arrives in few round-trips (sort/filter go
// complete a second later). Best of both: fast paint AND few requests for the bulk.
const FIRST_PAGE_SIZE = 25;
/** One empty list for every "no games yet" render, so memos keyed on the list don't recompute. */
const EMPTY_GAMES: GameRecord[] = [];
const GAMES_PAGE_SIZE = 500;

interface GamesQueryResult {
  games: GameRecord[];
  requiresUsername: boolean;
  nextToken: string | null;
}

/** Fetch user's saved games (DynamoDB when signed in, empty when signed out).
 *  Now queries by linked chess username. Returns requiresUsername flag
 *  if the user hasn't linked a chess account yet.
 *
 *  Uses cursor-based pagination: call loadMore() to fetch additional pages. */
/** What a since read can ask for: the home's list (the full view: its moments are the drill positions) or its Openings
 *  tile (the openings view, #339). */
export type SinceView = 'full' | 'openings';
const SINCE_KEY = { full: queryKeys.gamesSince, openings: queryKeys.gamesOpeningsSince } as const;

/**
 * The signed-in home's games: the 25 newest (header, last game, recent games, and "has any games at all"), then older
 * pages only while they're inside the window, each asked for with `since` so the server skips the rest. `useGames`
 * pages a player's WHOLE history in the background; for the home that was 4,222 games in nine ~10 s pages to show 90
 * days (2026-10-12). A separate cache from `useGames`, so the Library and Practice still get the full list. The
 * Openings tile reads its year this way too (`view: 'openings'`, #339). `enabled: false` asks nothing (the tile, for a
 * player with no linked account).
 *
 * A refetch keeps the list on screen (`firstPage` seeds the new first page over it) and pages the window again from
 * the new first page, refreshing each game in place (gamesSince.ts).
 */
export function useGamesSince(since: string, opts?: { readonly view?: SinceView; readonly enabled?: boolean }) {
  const { isSignedIn, getAuthHeaders } = useAuthToken();
  const queryClient = useQueryClient();
  const view = opts?.view ?? 'full';
  const key = SINCE_KEY[view](since);

  const query = useQuery<SinceRead<GameRecord>>({
    queryKey: key,
    queryFn: async (): Promise<SinceRead<GameRecord>> => {
      // covers null: an empty list asked before sign-in resolved says nothing about the account's games (#348).
      if (!isSignedIn) return { games: [], requiresUsername: false, nextToken: null, reached: null, covers: null };
      // Unfiltered on purpose: a player with no games in the window still has a last game to show.
      const page = await listUserGames(getAuthHeaders, FIRST_PAGE_SIZE, null, view);
      return firstPage(queryClient.getQueryData<SinceRead<GameRecord>>(key), page);
    },
    enabled: !!isSignedIn && (opts?.enabled ?? true),
    staleTime: staleTimes.standard,
  });

  const loadingRef = React.useRef(false);
  React.useEffect(() => {
    if (!query.data || loadingRef.current || !needsOlderGames(query.data, since)) return;
    loadingRef.current = true;
    (async () => {
      // The cursor is the cache's, read each time round: a refetch that lands meanwhile restarts the round.
      for (;;) {
        const current = queryClient.getQueryData<SinceRead<GameRecord>>(key);
        if (!current || !needsOlderGames(current, since)) break;
        const token = current.nextToken;
        const page = await listUserGames(getAuthHeaders, GAMES_PAGE_SIZE, token, view, since);
        // A refetch restarted the round while this page was on its way: drop it and go on from the new cursor. By the
        // entry, not the token: the new first page usually ends where the old one did, so its token is the same.
        if (queryClient.getQueryData<SinceRead<GameRecord>>(key) !== current) continue;
        queryClient.setQueryData<SinceRead<GameRecord>>(key, olderPage(current, page));
      }
    })()
      // A failed older page leaves the games already shown (homeStatus: a later page failing isn't an error).
      .catch(() => {})
      .finally(() => { loadingRef.current = false; });
  }, [query.data?.nextToken, since]); // eslint-disable-line react-hooks/exhaustive-deps -- re-run only when a page lands or the window moves

  return {
    ...query,
    data: query.data?.games ?? EMPTY_GAMES,
    requiresUsername: query.data?.requiresUsername ?? false,
  };
}

const GAMES_KEY = { full: queryKeys.games, card: queryKeys.gamesCard, openings: queryKeys.gamesOpenings, mistakes: queryKeys.gamesMistakes } as const;

/** The whole history, newest first: a small first page, then the rest in the background. `enabled: false` asks nothing
 *  and pages nothing, but still returns what's cached (the Library reads it only once a visit asks for every game, #351). */
export function useGames(opts?: { readonly view?: GamesView; readonly enabled?: boolean }) {
  const { isSignedIn, getAuthHeaders } = useAuthToken();
  const queryClient = useQueryClient();
  // 'card' = light projection (no moment arrays) — the Library grid (5.6x smaller); 'openings' = header fields + the
  // first 24 SANs, the Openings page. Each keys a SEPARATE cache from the full read, so Practice (which needs moments)
  // is unaffected.
  const view = opts?.view ?? 'full';
  const key = GAMES_KEY[view];
  const enabled = !!isSignedIn && (opts?.enabled ?? true);

  const query = useQuery<GamesQueryResult>({
    queryKey: key,
    queryFn: async (): Promise<GamesQueryResult> => {
      if (!isSignedIn) return { games: [], requiresUsername: false, nextToken: null };
      // Small first page — first paint waits only on this. The rest streams in below. On a REFETCH the page
      // lands on top of the list already shown (gamesPaging.ts) instead of replacing it with 25 games.
      const data = await listUserGames(getAuthHeaders, FIRST_PAGE_SIZE, null, view);
      const prev = queryClient.getQueryData<GamesQueryResult>(key);
      return {
        games: seedFromPrevious(prev?.games, data.games || []),
        requiresUsername: data.requiresUsername || false,
        nextToken: data.nextToken || null,
      };
    },
    enabled,
    staleTime: staleTimes.standard,
  });

  // Page the rest in the background, so sort/filter/search work on the whole library. Each page refreshes its
  // games in place. The guard covers one run, not the hook's life: it used to be set once, so after a refetch
  // the list stayed at its first 25 games until a remount.
  const loadingRef = React.useRef(false);
  // A page still failing after its retries stops the paging until resume() (the Library says it couldn't count).
  const [failed, setFailed] = React.useState(false);
  const [round, setRound] = React.useState(0);
  React.useEffect(() => {
    if (!enabled || !query.data?.nextToken || loadingRef.current) return;
    loadingRef.current = true;
    setFailed(false);
    (async () => {
      let token: string | null = query.data!.nextToken;
      while (token) {
        const at: string = token;
        const data = await retried(() => listUserGames(getAuthHeaders, GAMES_PAGE_SIZE, at, view), PAGE_RETRY_MS);
        const current = queryClient.getQueryData<GamesQueryResult>(key);
        if (!current) break;
        token = data.nextToken || null;
        queryClient.setQueryData<GamesQueryResult>(key, {
          games: mergePage(current.games, data.games || []),
          requiresUsername: false,
          nextToken: token,
        });
      }
    })()
      // A page that failed every try leaves the games already shown, and says so.
      .catch(() => { setFailed(true); })
      .finally(() => { loadingRef.current = false; });
  }, [query.data?.nextToken, enabled, round]); // eslint-disable-line react-hooks/exhaustive-deps -- re-run only when a new first page lands, it's switched on, or resume()
  const resume = React.useCallback(() => setRound((r) => r + 1), []);

  return {
    ...query,
    data: query.data?.games || [],
    requiresUsername: query.data?.requiresUsername || false,
    /** Every page is in (the background paging has reached the end). */
    complete: query.isSuccess && !query.data?.nextToken,
    /** A page failed every try: the paging has stopped short of the end. */
    pagingFailed: failed,
    /** Page on from where it stopped. */
    resume,
  };
}

/** The SET of gameOriginIds the user has analyzed — the cheap "is this analyzed?" query.
 *  Use this (not useGames) when you only need analyzed-ness, e.g. the landing "Reviewed" tag.
 *  useGames auto-pages the WHOLE library (~10-15s cold for 562 games, moment arrays and all); this
 *  hits /games/analyzed-ids (player-games lookup table only), so it's sub-second. */
export function useAnalyzedIds() {
  const { isSignedIn, getAuthHeaders } = useAuthToken();
  const query = useQuery<Set<string>>({
    queryKey: queryKeys.analyzedIds,
    queryFn: async () => {
      if (!isSignedIn) return new Set<string>();
      const data = await listAnalyzedIds(getAuthHeaders);
      return new Set(data.ids ?? []);
    },
    enabled: !!isSignedIn,
    staleTime: staleTimes.standard,
  });
  return { ...query, data: query.data ?? new Set<string>() };
}

/** Save a game. Invalidates games cache on success. */
export function useSaveGame() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthToken();

  return useMutation({
    mutationFn: async (game: GameRecord) => {
      return apiSaveGame(game, getAuthHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.games });
    },
  });
}


// ============================================================================
// USER PROFILE (users/me — tier, usage, preferences)
// ============================================================================

/** Fetch current user's profile (lazy-created on first call). */
export function useUserProfile() {
  const { isSignedIn, getAuthHeaders } = useAuthToken();

  return useQuery<UserProfile>({
    queryKey: queryKeys.userProfile,
    queryFn: async () => {
      const data = await getUserProfile(getAuthHeaders);
      // Backend wraps response in { user: { ...fields } }
      const raw = data.user;
      if (!raw || !raw.userId) {
        throw new Error('Invalid user profile response from server');
      }
      const tier = raw.tier.toLowerCase();
      const profile: UserProfile = {
        userId: raw.userId,
        // Preserve the canonical 'pro'; only collapse unknown values to 'free'. (Legacy rows may
        // still carry 'paid' — isPaidTier() treats both as paid.)
        tier: tier === 'pro' ? 'pro' : tier === 'paid' ? 'paid' : 'free',
        subscriptionStatus: raw.subscriptionStatus,
        stripeCustomerId: raw.stripeCustomerId ?? null,
        gamesAnalyzedThisMonth: raw.gamesAnalyzedThisMonth,
        coachCallsThisMonth: raw.coachCallsThisMonth,
        currentMonthKey: raw.currentMonthKey ?? '',
        preferences: {
          defaultDepth: raw.defaultDepth ?? raw.preferences?.defaultDepth,
          autoFlipBoard: raw.autoFlipBoard ?? raw.preferences?.autoFlipBoard,
          showEvalBar: raw.showEvalBar ?? raw.preferences?.showEvalBar,
        },
        // chessUsername FIRST: that's what set_chess_username writes. chesscomUsername is a dead
        // field nothing populates (see schemas/users.py) — preferring it read undefined for everyone.
        chessUsername: raw.chessUsername ?? raw.chesscomUsername,
        chessPlatform: raw.chessPlatform,
        activeBackfillJobId: raw.activeBackfillJobId ?? null,
        createdAt: raw.createdAt ?? '',
        updatedAt: raw.updatedAt ?? '',
      };
      return profile;
    },
    enabled: !!isSignedIn,
    staleTime: staleTimes.userProfile,
  });
}

/** Update user preferences. Invalidates profile cache on success. */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthToken();

  return useMutation({
    mutationFn: async (body: UpdateUserRequest) => {
      return updateUserProfile(body, getAuthHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userProfile });
    },
  });
}

// ============================================================================
// PLAYER PROFILE (games/profile — aggregated game stats)
// ============================================================================

// ============================================================================
// SHOWCASE (public — no auth)
// ============================================================================

/** Fetch showcase player list. Never auto-refetches (data is static). */
export function useShowcase() {
  return useQuery<ShowcasePlayer[]>({
    queryKey: queryKeys.showcase,
    queryFn: async () => {
      const data = await getShowcasePlayers();
      return data.players || [];
    },
    staleTime: staleTimes.showcase,
  });
}

/** Fetch detailed showcase player profile. */
export function useShowcasePlayer(username: string | null) {
  return useQuery<ShowcasePlayerDetail>({
    queryKey: queryKeys.showcasePlayer(username || ''),
    queryFn: async () => {
      const data = await getShowcasePlayer(username!);
      return data as unknown as ShowcasePlayerDetail;
    },
    enabled: !!username,
    staleTime: staleTimes.showcase,
  });
}

/** One game's full record (GET /games/{id}: allMoves, the coach's card text — what the list strips). Cached
 *  under the same key the Review page's findCachedAnalysis reads, so a game the home opened is already
 *  there when "Review the game" lands. `null` id → idle. */
export function useGameRecord(gameOriginId: string | null) {
  const { isSignedIn, getAuthHeaders } = useAuthToken();
  return useQuery<GameRecord>({
    queryKey: queryKeys.analysis(gameOriginId ?? ''),
    // The route wraps the record ({ game }); caching the wrapper left the home's graph and coach line empty
    // (seen signed in, 2026-10-12) and would have put a non-record under the key Review reads.
    queryFn: async () => {
      const { game } = await getGame(gameOriginId!, getAuthHeaders);
      if (!game) throw new Error(`game not found: ${gameOriginId}`);
      return game;
    },
    enabled: !!isSignedIn && !!gameOriginId,
    staleTime: staleTimes.standard,
  });
}
